import { Pencil, Trash2, Copy, Archive, ArchiveRestore, MoreVertical } from "lucide-react";
import { useState, useRef, useEffect } from "react";

/**
 * Standardised row-actions cluster used across every list page.
 *
 * Props:
 *   onEdit        — required; opens the Edit dialog with row prefilled
 *   onDelete      — required; hard-delete (with your own confirm dialog)
 *   onDuplicate   — optional; opens Add dialog prefilled without an ID
 *   onArchive     — optional; soft-delete (Archive)
 *   onRestore     — optional; only relevant on archived rows
 *   archived      — bool; when true, hides Edit/Delete and shows Restore
 *   canEdit       — role gate for Edit/Duplicate
 *   canDelete     — role gate for hard-Delete (owner only)
 *   compact       — bool; if true, renders as a 3-dot menu instead of inline icons
 *   testIdBase    — prefix for data-testid (e.g. "sub" → "sub-edit-{id}")
 *   id            — row id (used for data-testid)
 */
export default function RowActions({
    onEdit, onDelete, onDuplicate, onArchive, onRestore,
    archived = false, canEdit = true, canDelete = true,
    compact = false, testIdBase = "row", id = "",
}) {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    if (compact) {
        return (
            <div className="relative inline-block" ref={menuRef}>
                <button
                    onClick={() => setOpen((v) => !v)}
                    className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                    data-testid={`${testIdBase}-menu-${id}`}
                    aria-label="Actions"
                >
                    <MoreVertical size={13} />
                </button>
                {open && (
                    <div className="absolute right-0 mt-1 z-20 w-40 rounded-md border border-stone-200 bg-white shadow-lg py-1 text-[12.5px]"
                         data-testid={`${testIdBase}-menu-open-${id}`}>
                        {!archived && canEdit && onEdit && (
                            <MenuItem icon={Pencil} label="Edit" onClick={() => { setOpen(false); onEdit(); }} testId={`${testIdBase}-menu-edit-${id}`} />
                        )}
                        {!archived && canEdit && onDuplicate && (
                            <MenuItem icon={Copy} label="Duplicate" onClick={() => { setOpen(false); onDuplicate(); }} testId={`${testIdBase}-menu-dup-${id}`} />
                        )}
                        {!archived && onArchive && (
                            <MenuItem icon={Archive} label="Archive" onClick={() => { setOpen(false); onArchive(); }} testId={`${testIdBase}-menu-arch-${id}`} />
                        )}
                        {archived && onRestore && (
                            <MenuItem icon={ArchiveRestore} label="Restore" onClick={() => { setOpen(false); onRestore(); }} testId={`${testIdBase}-menu-rest-${id}`} />
                        )}
                        {canDelete && onDelete && (
                            <MenuItem icon={Trash2} label="Delete" tone="danger" onClick={() => { setOpen(false); onDelete(); }} testId={`${testIdBase}-menu-del-${id}`} />
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="inline-flex items-center gap-1">
            {!archived && canEdit && onEdit && (
                <button onClick={onEdit} title="Edit"
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                        data-testid={`${testIdBase}-edit-${id}`}>
                    <Pencil size={13} />
                </button>
            )}
            {!archived && canEdit && onDuplicate && (
                <button onClick={onDuplicate} title="Duplicate"
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                        data-testid={`${testIdBase}-duplicate-${id}`}>
                    <Copy size={13} />
                </button>
            )}
            {!archived && onArchive && (
                <button onClick={onArchive} title="Archive"
                        className="p-1.5 rounded hover:bg-amber-50 text-stone-500 hover:text-amber-700"
                        data-testid={`${testIdBase}-archive-${id}`}>
                    <Archive size={13} />
                </button>
            )}
            {archived && onRestore && (
                <button onClick={onRestore} title="Restore"
                        className="p-1.5 rounded hover:bg-emerald-50 text-stone-500 hover:text-emerald-700"
                        data-testid={`${testIdBase}-restore-${id}`}>
                    <ArchiveRestore size={13} />
                </button>
            )}
            {canDelete && onDelete && (
                <button onClick={onDelete} title="Delete"
                        className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600"
                        data-testid={`${testIdBase}-delete-${id}`}>
                    <Trash2 size={13} />
                </button>
            )}
        </div>
    );
}

function MenuItem({ icon: Icon, label, onClick, tone, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`w-full text-left px-2.5 py-1.5 inline-flex items-center gap-2 hover:bg-stone-50 ${
                tone === "danger" ? "text-red-700 hover:bg-red-50" : "text-stone-700"
            }`}
        >
            <Icon size={12} /> {label}
        </button>
    );
}
